<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useTranslationPreferences } from 'dashboard/composables/useTranslationPreferences';
import {
  HUNYUAN_TRANSLATION_LANGUAGE_OPTIONS,
  normalizeHunyuanTranslationLanguage,
} from 'dashboard/constants/translationLanguages';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import FormSelect from 'v3/components/Form/Select.vue';
import translationPreferencesAPI from 'dashboard/api/translationPreferences';

const { t } = useI18n();
const route = useRoute();
const { setPreferences } = useTranslationPreferences();

const translationEnabled = ref(true);
const autoTranslateEnabled = ref(false);
const autoTranslateTargetLanguage = ref('zh');
const isLoading = ref(false);

const canEnableAutoTranslate = computed(() => translationEnabled.value);
const targetLanguageOptions = computed(() => [
  {
    name: t(
      'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.AUTO_TRANSLATE_TARGET_LANGUAGE.FOLLOW_UI_OPTION'
    ),
    iso_639_1_code: 'follow_ui',
  },
  ...HUNYUAN_TRANSLATION_LANGUAGE_OPTIONS.map(({ code, name }) => ({
    name,
    iso_639_1_code: code,
  })),
]);

const normalizePreferenceTargetLanguage = value => {
  if (!value) {
    return 'zh';
  }

  if (value.toString().trim().toLowerCase() === 'follow_ui') {
    return 'follow_ui';
  }

  return normalizeHunyuanTranslationLanguage(value) || 'zh';
};

const loadPreferences = async () => {
  try {
    const response = await translationPreferencesAPI.get();
    translationEnabled.value = response.data.translation_enabled ?? true;
    autoTranslateEnabled.value = response.data.auto_translate_enabled ?? false;
    autoTranslateTargetLanguage.value = normalizePreferenceTargetLanguage(
      response.data.auto_translate_target_language
    );
    setPreferences(response.data, { accountId: route.params.accountId });
  } catch (_error) {
    // Keep defaults when preferences cannot be loaded.
  }
};

const updatePreference = async (field, value) => {
  isLoading.value = true;
  try {
    const payload = {
      translation_enabled:
        field === 'translation_enabled' ? value : translationEnabled.value,
      auto_translate_enabled:
        field === 'auto_translate_enabled' ? value : autoTranslateEnabled.value,
      auto_translate_target_language:
        field === 'auto_translate_target_language'
          ? value
          : autoTranslateTargetLanguage.value,
    };

    // 关闭翻译功能时，同时关闭自动翻译
    if (field === 'translation_enabled' && !value) {
      payload.auto_translate_enabled = false;
    }

    const response = await translationPreferencesAPI.update(payload);

    translationEnabled.value = response.data.translation_enabled;
    autoTranslateEnabled.value = response.data.auto_translate_enabled;
    autoTranslateTargetLanguage.value = normalizePreferenceTargetLanguage(
      response.data.auto_translate_target_language
    );
    setPreferences(response.data, { accountId: route.params.accountId });

    useAlert(t('PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.UPDATE_ERROR'));
    await loadPreferences(); // 错误时回滚
  } finally {
    isLoading.value = false;
  }
};

const handleTranslationToggle = value => {
  updatePreference('translation_enabled', value);
};

const handleAutoTranslateToggle = value => {
  if (!canEnableAutoTranslate.value && value) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.REQUIRES_TRANSLATION')
    );
    return;
  }
  updatePreference('auto_translate_enabled', value);
};

const handleTargetLanguageChange = value => {
  updatePreference(
    'auto_translate_target_language',
    normalizePreferenceTargetLanguage(value)
  );
};

onMounted(() => {
  loadPreferences();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- 翻译功能开关 -->
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.TRANSLATION_ENABLED.LABEL'
            )
          }}
        </label>
        <p class="text-xs text-n-slate-11 mt-1">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.TRANSLATION_ENABLED.NOTE'
            )
          }}
        </p>
      </div>
      <Switch
        :model-value="translationEnabled"
        :disabled="isLoading"
        @change="handleTranslationToggle"
      />
    </div>

    <!-- 自动翻译开关 -->
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <label
          class="text-sm font-medium"
          :class="canEnableAutoTranslate ? 'text-n-slate-12' : 'text-n-slate-9'"
        >
          {{
            $t(
              'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.AUTO_TRANSLATE_ENABLED.LABEL'
            )
          }}
        </label>
        <p class="text-xs text-n-slate-11 mt-1">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.AUTO_TRANSLATE_ENABLED.NOTE'
            )
          }}
        </p>
      </div>
      <Switch
        :model-value="autoTranslateEnabled"
        :disabled="isLoading || !canEnableAutoTranslate"
        @change="handleAutoTranslateToggle"
      />
    </div>

    <div class="flex items-center justify-between gap-4">
      <div class="flex-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.AUTO_TRANSLATE_TARGET_LANGUAGE.LABEL'
            )
          }}
        </label>
        <p class="text-xs text-n-slate-11 mt-1">
          {{
            $t(
              'PROFILE_SETTINGS.FORM.TRANSLATION_SECTION.AUTO_TRANSLATE_TARGET_LANGUAGE.NOTE'
            )
          }}
        </p>
      </div>
      <FormSelect
        :model-value="autoTranslateTargetLanguage"
        name="auto-translate-target-language"
        spacing="compact"
        class="min-w-32 mt-px"
        :disabled="isLoading"
        :options="targetLanguageOptions"
        label=""
        @update:model-value="handleTargetLanguageChange"
      >
        <option
          v-for="option in targetLanguageOptions"
          :key="option.iso_639_1_code || 'follow_ui'"
          :value="option.iso_639_1_code"
          :selected="option.iso_639_1_code === autoTranslateTargetLanguage"
        >
          {{ option.name }}
        </option>
      </FormSelect>
    </div>
  </div>
</template>
